import React, { useState, useEffect } from 'react';
import { Container, Alert, Button } from 'react-bootstrap';
import { useAuth } from '../../../AuthContext/AuthContext';
import { useTranslation } from 'react-i18next';
import IdentificationFormFields from './IdentificationFormFields';
import {
    fetchIdentificationDocuments,
    saveIdentificationDocument,
    deleteIdentificationDocument,
    validateIdentificationFormData
} from './FormActions';
import { IDENTIFICATION_DEFAULT_VALUES } from './identificationConfig';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';

const IdentificationForm = ({ onSave, onValidationChange }) => {
    const { t } = useTranslation('identificationDoc');
    const { currentUser } = useAuth();

    // Use centralized default values from config
    const [formData, setFormData] = useState(IDENTIFICATION_DEFAULT_VALUES);

    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [dataFetched, setDataFetched] = useState(false);
    const [isFormEmpty, setIsFormEmpty] = useState(true); // Track if form is empty

    // Mount effect for initial data loading
    useEffect(() => {
        const loadData = async () => {
            if (!currentUser?.id) {
                console.log("No current user ID, skipping data fetch");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                console.log("Fetching identification documents...");
                console.log("Current user:", currentUser);

                const data = await fetchIdentificationDocuments();

                console.log("Fetched identification documents:", data);

                if (data) {
                    setFormData(data);
                    validateAndUpdateProgress(data);
                    console.log("Form data updated:", data);
                    setDataFetched(true);
                } else {
                    console.log("No data returned from fetchIdentificationDocuments");
                    setDataFetched(true);
                }
            } catch (err) {
                console.error("Error fetching identification documents:", err);
                setError(t('operation_failed'));
                setDataFetched(true);
            } finally {
                setLoading(false);
            }
        };

        loadData();

        console.log("Access token exists:", !!localStorage.getItem('accessToken'));
    }, [currentUser?.id, t]);

    const validateAndUpdateProgress = (data) => {
        if (!data) {
            console.log("No data to validate");
            setIsFormEmpty(true);
            if (onValidationChange) {
                onValidationChange(0);
            }
            return { isValid: true, errors: {}, percentage: 0, isEmpty: true };
        }

        console.log("Validating data:", data);
        const { errors, percentage, isEmpty } = validateIdentificationFormData(data);
        console.log("Validation result:", { errors, percentage, isEmpty });

        setFormErrors(errors);
        setIsFormEmpty(isEmpty);

        if (onValidationChange) {
            onValidationChange(percentage);
        }

        return { isValid: Object.keys(errors).length === 0, errors, percentage, isEmpty };
    };

    // Monitor formData changes for validation
    useEffect(() => {
        if (formData && Object.keys(formData).length > 0) {
            console.log("Form data changed, validating:", formData);
            validateAndUpdateProgress(formData);
        }
    }, [formData]);

    const handleAdd = () => {
        console.log("Add button clicked");
        // Use centralized default values instead of hardcoded object
        setFormData({ ...IDENTIFICATION_DEFAULT_VALUES });
        setIsEditing(true);
        setError(null); // Clear any previous errors
        setFormErrors({}); // Clear validation errors
    };

    const handleChange = (e) => {
        if (!isEditing) return;

        const { name, value } = e.target;
        console.log("Form field changed:", name, value);

        const updatedFormData = {
            ...formData,
            [name]: value
        };

        setFormData(updatedFormData);

        // Real-time validation update
        const { percentage, isEmpty } = validateIdentificationFormData(updatedFormData);
        console.log("Updated percentage after field change:", percentage);
        console.log("Form is empty:", isEmpty);

        setIsFormEmpty(isEmpty);

        if (onValidationChange) {
            onValidationChange(percentage);
        }

        // Clear field-specific errors when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }

        // Clear form-level error when user starts entering data
        if (formErrors._form && !isEmpty) {
            setFormErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors._form;
                return newErrors;
            });
        }
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            setError(null);
            console.log("Starting save process, formData:", formData);

            // Validate the form data and check for errors
            const { isValid, errors, percentage } = validateAndUpdateProgress(formData);
            console.log("Validation result:", { isValid, errors, percentage });

            // Update form errors state
            setFormErrors(errors);

            // Block submission if there are validation errors
            if (!isValid) {
                console.log("Validation failed, blocking save");
                setError(t('validation_error_fix') || 'Please fix the validation errors highlighted below.');
                setLoading(false);
                return; // Stop here, don't proceed with save
            }

            console.log("Validation passed, proceeding with save");
            console.log("About to call saveIdentificationDocument with:", formData);

            const savedData = await saveIdentificationDocument(formData, currentUser);
            console.log("Received saved data:", savedData);

            setFormData(savedData);

            // Recalculate percentage with saved data
            const { percentage: newPercentage } = validateIdentificationFormData(savedData);
            console.log("Post-save percentage:", newPercentage);

            if (onValidationChange) {
                onValidationChange(newPercentage);
            }

            setIsEditing(false);
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);

            console.log("Calling onSave with:", savedData);
            onSave && onSave(savedData);

            setFormErrors({}); // Clear errors on successful save
            setError(null); // Clear any previous error messages

        } catch (err) {
            console.error("Error in handleSave:", err);
            setError(t('failed_to_save_retry') || 'Failed to save identification documents. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            if (formData.id) {
                console.log("Deleting document with ID:", formData.id);
                await deleteIdentificationDocument(formData.id);
            } else {
                console.log("No document ID, skipping API delete call");
            }

            // Reset form data to initial empty state using centralized config
            const emptyFormData = { ...IDENTIFICATION_DEFAULT_VALUES };
            setFormData(emptyFormData);

            // Explicitly validate and update progress to 0%
            const { percentage } = validateIdentificationFormData(emptyFormData);
            console.log("Post-delete percentage:", percentage);
            if (onValidationChange) {
                onValidationChange(percentage); // Should be 0
            }

            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
            onSave && onSave(null); // Notify parent of deletion

            setFormErrors({});
            setError(null);
        } catch (err) {
            console.error("Error in handleDelete:", err);
            setError(t('operation_failed'));
        } finally {
            setLoading(false);
            setShowDeleteConfirm(false);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setError(null); // Clear errors
        setFormErrors({}); // Clear validation errors
        setIsFormEmpty(true); // Reset empty state

        if (formData.id) {
            // If we have an existing document, reload it from the server
            fetchIdentificationDocuments().then(data => {
                if (data) {
                    setFormData(data);
                    validateAndUpdateProgress(data);
                } else {
                    // Use centralized default values
                    setFormData({ ...IDENTIFICATION_DEFAULT_VALUES });
                    if (onValidationChange) {
                        onValidationChange(0);
                    }
                }
            }).catch(err => {
                console.error("Error refetching data on cancel:", err);
            });
        } else {
            // If no existing document, reset to empty form using centralized config
            setFormData({ ...IDENTIFICATION_DEFAULT_VALUES });
            if (onValidationChange) {
                onValidationChange(0);
            }
        }
    };

    const hasAnyData = () => {
        if (!formData) return false;

        return Object.values(formData).some(value => {
            if (typeof value === 'string') {
                return value.trim() !== '';
            }
            return value !== null && value !== undefined;
        });
    };

    // Prepare a list of validation error messages for display (excluding form-level errors)
    const getValidationErrorMessages = () => {
        return Object.entries(formErrors)
            .filter(([field, message]) => field !== '_form') // Exclude form-level errors
            .map(([field, message]) => `• ${message}`);
    };

    console.log("Render state:", {
        loading,
        hasFormData: !!formData,
        formDataKeys: formData ? Object.keys(formData) : [],
        hasNationalPassport: formData?.national_passport ? true : false,
        isEditing,
        dataFetched,
        hasAnyData: hasAnyData(),
        formErrorsCount: Object.keys(formErrors).length,
        isFormEmpty
    });

    return (
        <Container className="mt-4 mb-5">
            <h2 className="text-center mb-4" style={{ fontWeight: 'bold', color: '#4682B4' }}>
                {t('identification_documents_title')}
            </h2>

            {/* Add custom CSS for disabled button styling */}
            <style>
                {`
                    .disabled-button {
                        opacity: 0.6;
                        cursor: not-allowed !important;
                    }
                    .disabled-button:hover {
                        opacity: 0.6 !important;
                    }
                `}
            </style>

            {loading && <Alert variant="info">{t('loading')}</Alert>}

            {error && (
                <Alert variant="danger" className="text-center">
                    <strong>{t('error') || 'Error'}:</strong> {error}

                    {Object.keys(formErrors).length > 0 && !formErrors._form && (
                        <div className="mt-2">
                            <p className="mb-1">{t('please_correct_fields') || 'Please correct the following fields:'}:</p>
                            <ul className="mb-0 text-left">
                                {getValidationErrorMessages().map((message, index) => (
                                    <li key={index}>{message}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </Alert>
            )}

            {success && <Alert variant="success">{t('operation_successful')}</Alert>}

            {!loading && dataFetched && !hasAnyData() && !isEditing && (
                <>
                    <Alert variant="warning" className="text-center">
                        {t('no_documents_warning')}
                    </Alert>
                    <div className="d-flex justify-content-center">
                        <Button variant="success" onClick={handleAdd}>
                            <FaPlus className="me-2" />
                            {t('add_identification_documents')}
                        </Button>
                    </div>
                </>
            )}

            {(isEditing || hasAnyData()) && (
                <div className="border rounded p-4 bg-white shadow-sm">
                    <IdentificationFormFields
                        formData={formData}
                        handleChange={handleChange}
                        errors={formErrors}
                        readOnly={!isEditing}
                    />

                    <div className="d-flex justify-content-end mt-4 gap-2">
                        {isEditing ? (
                            <>
                                <Button
                                    variant="success"
                                    onClick={handleSave}
                                    disabled={loading || isFormEmpty} // Disable if loading OR form is empty
                                    className={isFormEmpty ? 'disabled-button' : ''}
                                    title={isFormEmpty ? t('please_fill_one_document') || 'Please fill in at least one identification document' : ''}
                                >
                                    {loading ? t('saving') || 'Saving...' : t('save')}
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={handleCancel}
                                    disabled={loading}
                                >
                                    {t('cancel')}
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="primary"
                                    onClick={() => setIsEditing(true)}
                                    disabled={loading}
                                >
                                    <FaEdit className="me-2" />
                                    {t('edit')}
                                </Button>
                                <Button
                                    variant="danger"
                                    onClick={() => setShowDeleteConfirm(true)}
                                    disabled={loading}
                                >
                                    <FaTrash className="me-2" />
                                    {t('delete')}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {showDeleteConfirm && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{t('confirm_delete')}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowDeleteConfirm(false)}></button>
                            </div>
                            <div className="modal-body">{t('confirm_delete_message')}</div>
                            <div className="modal-footer">
                                <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                                    {t('cancel')}
                                </Button>
                                <Button variant="danger" onClick={handleDelete}>
                                    {t('delete')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Container>
    );
};

export default IdentificationForm;